import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useCreateBanquetRequest } from "@workspace/api-client-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

const banquetSchema = z.object({
  name: z.string().min(1, "Обязательное поле"),
  phone: z.string().min(1, "Обязательное поле"),
  eventType: z.string().min(1, "Выберите формат"),
  date: z.string().min(1, "Укажите дату"),
  guests: z.string().min(1, "Укажите гостей"),
  comment: z.string().optional(),
  consent: z.boolean().refine((v) => v === true, { message: "Необходимо ваше согласие" }),
});
type BanquetFormValues = z.infer<typeof banquetSchema>;

export default function Banket() {
  const [loaded, setLoaded] = useState(false);
  const createBanquet = useCreateBanquetRequest();

  const [calcStep, setCalcStep] = useState<"calc" | "details" | "form">("calc");

  // Step 1 — calculator
  const [calcEventType, setCalcEventType] = useState("Свадьба");
  const [calcFormat, setCalcFormat] = useState("Банкет с рассадкой");
  const [calcGuests, setCalcGuests] = useState(50);
  const [calcDayType, setCalcDayType] = useState<"weekday" | "weekend">("weekend");
  const [calcExtras, setCalcExtras] = useState<string[]>([]);

  // Step 2 — event details
  const [menuType, setMenuType] = useState("Смешанное");
  const [menuCourses, setMenuCourses] = useState("4 блюда");
  const [welcomeCocktail, setWelcomeCocktail] = useState(false);
  const [seatingType, setSeatingType] = useState("Круглые столы");
  const [seatingPerTable, setSeatingPerTable] = useState("8 мест");
  const [startTime, setStartTime] = useState("18:00");
  const [duration, setDuration] = useState("4 часа");
  const [entertainment, setEntertainment] = useState(false);
  const [allergies, setAllergies] = useState("");
  const [vipNote, setVipNote] = useState("");

  const EVENT_TYPES = ["Свадьба", "День рождения", "Корпоратив", "Юбилей", "Другое"];
  const FORMATS = ["Банкет с рассадкой", "Фуршет", "Коктейльная вечеринка"];
  const EXTRAS_LIST = ["Живая музыка", "Декор зала", "Ведущий", "Проектор / экран", "Фотограф"];

  const toggleExtra = (e: string) =>
    setCalcExtras(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  function getHallName(g: number) {
    if (g <= 30) return "Малый зал (до 30 гостей)";
    if (g <= 80) return "Основной зал (до 80 гостей)";
    if (g <= 150) return "Большой зал + открытая терраса (до 150 гостей)";
    return "Весь ресторан — все залы и терраса (до 300 гостей)";
  }

  function calcPrice() {
    const rateMin = calcFormat === "Фуршет" ? 2600 : calcFormat === "Коктейльная вечеринка" ? 2000 : 3500;
    const rateMax = calcFormat === "Фуршет" ? 3800 : calcFormat === "Коктейльная вечеринка" ? 3000 : 5200;
    const hallMin = calcGuests <= 30 ? 35000 : calcGuests <= 80 ? 65000 : calcGuests <= 150 ? 120000 : 220000;
    const hallMax = calcGuests <= 30 ? 55000 : calcGuests <= 80 ? 100000 : calcGuests <= 150 ? 180000 : 350000;
    let min = hallMin + rateMin * calcGuests;
    let max = hallMax + rateMax * calcGuests;
    if (calcDayType === "weekend") { min *= 1.15; max *= 1.15; }
    if (calcExtras.includes("Живая музыка")) { min += 28000; max += 50000; }
    if (calcExtras.includes("Декор зала")) { min += 18000; max += 38000; }
    if (calcExtras.includes("Ведущий")) { min += 38000; max += 68000; }
    if (calcExtras.includes("Проектор / экран")) { min += 7000; max += 14000; }
    if (calcExtras.includes("Фотограф")) { min += 24000; max += 42000; }
    return {
      min: Math.round(min / 5000) * 5000,
      max: Math.round(max / 5000) * 5000,
    };
  }

  function buildManagerComment() {
    const { min, max } = calcPrice();
    const fmt = (n: number) => n.toLocaleString("ru-RU");
    const isSeated = calcFormat === "Банкет с рассадкой";
    return [
      `[БАНКЕТНЫЙ БРИФ]`,
      ``,
      `── МЕРОПРИЯТИЕ ──`,
      `Тип: ${calcEventType}`,
      `Формат: ${calcFormat}`,
      `Гостей: ${calcGuests}`,
      `День: ${calcDayType === "weekend" ? "Выходной (пт–вс)" : "Будний день (пн–чт)"}`,
      `Зал: ${getHallName(calcGuests)}`,
      calcExtras.length ? `Доп. услуги: ${calcExtras.join(", ")}` : null,
      ``,
      `── МЕНЮ ──`,
      `Тип меню: ${menuType}`,
      `Подачи: ${menuCourses}`,
      `Приветственный коктейль: ${welcomeCocktail ? "Да" : "Нет"}`,
      ``,
      isSeated ? `── РАССАДКА ──` : null,
      isSeated ? `Тип столов: ${seatingType}` : null,
      isSeated ? `Мест за столом: ${seatingPerTable}` : null,
      ``,
      `── ТАЙМИНГ ──`,
      `Начало: ${startTime}`,
      `Продолжительность: ${duration}`,
      `Развлекательная программа: ${entertainment ? "Да" : "Нет"}`,
      ``,
      allergies ? `── ОСОБЫЕ ТРЕБОВАНИЯ ──` : null,
      allergies ? `Аллергии / ограничения: ${allergies}` : null,
      vipNote ? `VIP-гости / пожелания: ${vipNote}` : null,
      ``,
      `── БЮДЖЕТ ──`,
      `Расчётный: от ${fmt(min)} до ${fmt(max)} ₽`,
    ].filter(v => v !== null).join("\n");
  }

  function goToDetails() {
    setCalcStep("details");
    setTimeout(() => document.getElementById("banquet-form")?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  function goToContactForm() {
    form.setValue("eventType", calcEventType);
    form.setValue("guests", String(calcGuests));
    form.setValue("comment", buildManagerComment());
    setCalcStep("form");
    setTimeout(() => document.getElementById("banquet-form")?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  useSeo({
    title: "Банкет и мероприятия — Паруса на крыше | День рождения на крыше СПб",
    description: "Банкетный зал на крыше в Санкт-Петербурге. День рождения, свадьба, корпоратив с панорамным видом на город. До 300 гостей. Организация под ключ. +7 (812) 991-10-90.",
    keywords: "банкет на крыше Петербург, день рождения на крыше СПб, свадьба на крыше Петербург, корпоратив ресторан СПб, банкетный зал панорамный вид, аренда зала ресторан Петербург, мероприятия на крыше",
    image: "/photos/view_summer.jpg",
    canonical: "/banket",
  });
  useBreadcrumbs([{ name: "Банкет и мероприятия", path: "/banket" }]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
  }, []);

  const form = useForm<BanquetFormValues>({
    resolver: zodResolver(banquetSchema),
    defaultValues: { name: "", phone: "", eventType: "", date: "", guests: "", comment: "", consent: false },
  });

  const onSubmit = (values: BanquetFormValues) => {
    createBanquet.mutate({ data: values }, { onSuccess: () => form.reset() });
  };

  return (
    <div className="w-full bg-background relative z-10">
      <section className="relative min-h-[80svh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20 pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute inset-0 z-0">
          <img src="/photos/view_summer.webp" alt="" className="w-full h-full object-cover" style={{ filter: "blur(10px) brightness(0.2) saturate(0.5)", transform: "scale(1.05)" }} loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,20,10,0.95) 0%, rgba(10,30,18,0.9) 100%)" }} />
        </div>
        <div className="absolute right-10 top-1/2 -translate-y-1/2 font-serif text-[420px] font-light leading-none text-primary/5 select-none pointer-events-none tracking-tighter z-0 hidden lg:block">З</div>
        <div className="relative z-10 w-full max-w-6xl text-center px-6 md:px-14" style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1s ease 0.3s" }}>
          <div className="inline-block px-6 py-2 border border-primary/30 mb-6"><span className="text-[9px] tracking-[0.3em] uppercase text-accent">Частные события</span></div>
          <h1 className="text-[clamp(2.5rem,5.5vw,5rem)] font-serif font-light tracking-wide text-foreground mb-4 max-w-5xl mx-auto leading-[1.1]">Банкет и мероприятия на крыше в Петербурге</h1>
          <p className="text-xl md:text-2xl font-serif font-light text-foreground/60 mb-8 max-w-2xl mx-auto leading-relaxed italic">Праздник над городом</p>
          <div className="flex flex-col lg:flex-row flex-wrap items-center justify-center gap-4">
            <button onClick={() => document.getElementById("banquet-form")?.scrollIntoView({ behavior: "smooth" })} className="bg-primary/20 border border-primary text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-primary-foreground transition-colors">
              Запросить расчет
            </button>
            <a
              href="tel:+78129225263"
              className="flex items-center justify-center gap-2 max-w-full min-h-11 text-center leading-relaxed text-foreground/50 hover:text-accent transition-colors text-xs tracking-[0.15em]"
              onClick={() =>
                typeof (window as any).ym === "function" &&
                (window as any).ym(109746755, "reachGoal", "banket_phone_click")
              }
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.9a16 16 0 0 0 6 6l1.27-.82a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.02z"/>
              </svg>
              +7 (812) 922-52-63 — банкетный менеджер
            </a>
          </div>
        </div>
      </section>

      <section className="py-32 bg-card relative z-10 border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[{ title: "Свадьба", desc: "Идеальное пространство для выездной регистрации и ужина с видом на закат.", href: "/svadba" },
              { title: "День рождения", desc: "Камерные залы для близких или масштабная вечеринка с живой музыкой.", href: "/den-rozhdeniya" },
              { title: "Корпоратив", desc: "Залы до 300 человек, сцена, профессиональное оборудование.", href: "/korporativ" }].map((f, i) => (
              <Link key={i} href={f.href} className="border border-primary/20 p-10 bg-background/50 group block hover:border-primary/50 transition-colors">
                <div className="font-serif text-5xl font-light text-primary/20 mb-6 group-hover:text-primary/40 transition-colors">0{i+1}</div>
                <h3 className="font-serif text-3xl font-light mb-4 text-foreground">{f.title}</h3>
                <p className="text-sm font-light text-muted-foreground leading-relaxed mb-4">{f.desc}</p>
                <span className="text-[10px] tracking-[0.2em] uppercase text-accent/60 group-hover:text-accent transition-colors">Подробнее →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BIRTHDAY OFFER */}
      <section className="py-32 bg-background relative z-10 border-b border-primary/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 font-serif text-[300px] md:text-[420px] font-light leading-none text-primary/[0.03] select-none tracking-tighter translate-x-20 -translate-y-10">ДР</div>
        </div>
        <div className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Специальное предложение</div>
              <h2 className="text-4xl md:text-6xl font-serif font-light text-primary mb-6 leading-tight">
                День рождения<br />на крыше
              </h2>
              <div className="w-20 h-[1px] bg-primary/30 mb-8" />
              <p className="text-base text-muted-foreground font-light leading-relaxed mb-6">
                За <span className="text-accent">7 дней до</span> и <span className="text-accent">7 дней после</span> вашего дня рождения мы дарим особые бонусы — просто потому что этот день заслуживает большего.
              </p>
              <p className="text-xs text-muted-foreground/50 font-light leading-relaxed">
                Акция действует при оплате счёта наличными, независимо от суммы.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
                <a
                  href="https://remarked.ru/widget/new/points/726106/726106.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-background transition-colors px-10 py-4 text-[10px] tracking-[0.3em] uppercase font-medium"
                >
                  Забронировать стол →
                </a>
                <Link href="/den-rozhdeniya" className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-accent transition-colors py-4">
                  Подробнее о днях рождения →
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { num: "01", title: "Скидка 20%", desc: "На весь ваш счёт в день рождения" },
                { num: "02", title: "Бонусная карта", desc: "Персональная карта гостя ресторана" },
                { num: "03", title: "Кусочек торта", desc: "От шеф-кондитера в подарок" },
                { num: "04", title: "Поздравление", desc: "Персональное — от всей команды" },
              ].map((gift) => (
                <div key={gift.num} className="border border-primary/20 p-6 bg-card/50 hover:border-primary/50 transition-colors group">
                  <div className="font-serif text-3xl font-light text-primary/20 mb-3 group-hover:text-primary/40 transition-colors">{gift.num}</div>
                  <h4 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-2">{gift.title}</h4>
                  <p className="text-xs text-muted-foreground font-light leading-relaxed">{gift.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WEDDINGS */}
      <section className="py-32 bg-card relative z-10 border-b border-primary/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none">
          <div className="absolute bottom-0 left-0 font-serif text-[280px] font-light leading-none text-primary/[0.03] tracking-tighter -translate-x-10 translate-y-10">С</div>
        </div>
        <div className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img src="/photos/view_summer.webp" alt="Свадьба на крыше Петербурга" className="w-full aspect-[4/3] object-cover border border-primary/15" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Свадьба</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-6 leading-tight">
                Ваш день —<br /><span className="text-accent italic">над крышами города</span>
              </h2>
              <div className="w-20 h-[1px] bg-primary/30 mb-8" />
              <p className="text-base text-muted-foreground font-light leading-relaxed mb-5">
                Выездная регистрация на открытой террасе с панорамой Петербурга, банкет с авторским меню, живая музыка и закат над Невой — всё это в одном месте, на 6-м этаже Olympic Plaza.
              </p>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-10">
                Мы берём на себя организацию под ключ: декор, кейтеринг, артисты, фотозона. Вам остаётся только наслаждаться.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <button
                  onClick={() => document.getElementById("banquet-form")?.scrollIntoView({ behavior: "smooth" })}
                  className="bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-background transition-colors px-10 py-4 text-[10px] tracking-[0.3em] uppercase font-medium"
                >
                  Запросить расчёт →
                </button>
                <Link href="/svadba" className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-accent transition-colors py-4">
                  Подробнее о свадьбах →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORPORATES */}
      <section className="py-32 bg-background relative z-10 border-b border-primary/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none">
          <div className="absolute top-0 right-0 font-serif text-[280px] font-light leading-none text-primary/[0.03] tracking-tighter translate-x-10 -translate-y-10">К</div>
        </div>
        <div className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Корпоратив</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-6 leading-tight">
                Корпоратив,<br /><span className="text-accent italic">который запомнят</span>
              </h2>
              <div className="w-20 h-[1px] bg-primary/30 mb-8" />
              <p className="text-base text-muted-foreground font-light leading-relaxed mb-5">
                Залы до 300 гостей, профессиональная сцена и звуковое оборудование, индивидуальное меню. Полное закрытие ресторана для вашей команды — с видом на город, который не забыть.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-10">
                {[
                  { label: "До 300 гостей", sub: "Вместимость залов" },
                  { label: "Сцена и звук", sub: "Профессиональное оборудование" },
                  { label: "Меню под заказ", sub: "Индивидуальный кейтеринг" },
                  { label: "Организация", sub: "Под ключ от нашей команды" },
                ].map((item) => (
                  <div key={item.label} className="border-l border-primary/30 pl-4">
                    <div className="text-accent text-sm font-medium mb-1">{item.label}</div>
                    <div className="text-muted-foreground text-xs font-light">{item.sub}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <button
                  onClick={() => document.getElementById("banquet-form")?.scrollIntoView({ behavior: "smooth" })}
                  className="bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-background transition-colors px-10 py-4 text-[10px] tracking-[0.3em] uppercase font-medium"
                >
                  Запросить расчёт →
                </button>
                <Link href="/korporativ" className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-accent transition-colors py-4">
                  Подробнее о корпоративах →
                </Link>
              </div>
            </div>
            <div>
              <img src="/photos/view_clouds.webp" alt="Корпоратив на крыше Петербурга" className="w-full aspect-[4/3] object-cover border border-primary/15" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* DEPOSIT REGULATIONS */}
      <section className="py-32 bg-card relative z-10 border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Сезон 2026</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-4">Регламент условий</h2>
            <p className="text-sm text-muted-foreground font-light max-w-xl">
              Минимальные депозитные суммы для проведения банкетов и частных мероприятий. Все суммы могут корректироваться в зависимости от даты, сезона и формата события.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-primary/10">
            {[
              {
                num: "01",
                title: "Банкетный зал",
                sub: "2 этаж",
                prices: [{ label: "Будни / воскресенье", value: "от 200 000 ₽" }, { label: "Пятница / суббота", value: "от 200 000 ₽" }],
                note: "Акция до 10.12.2026",
              },
              {
                num: "02",
                title: "VIP-зал",
                sub: "1 этаж · до 20 человек",
                prices: [{ label: "Будни / воскресенье", value: "60 000 ₽" }, { label: "Пятница / суббота", value: "80 000 ₽" }],
              },
              {
                num: "03",
                title: "VIP-зал",
                sub: "2 этаж · до 10–12 человек",
                prices: [{ label: "Будни / воскресенье", value: "30 000 ₽" }, { label: "Пятница / суббота", value: "50 000 ₽" }],
              },
              {
                num: "04",
                title: "Полное закрытие террасы",
                sub: null,
                prices: [{ label: "Будни / воскресенье", value: "1 000 000 ₽" }, { label: "Пятница / суббота", value: "1 500 000 ₽" }],
                note: "включая аренду площадки",
              },
              {
                num: "05",
                title: "Полное закрытие ресторана",
                sub: "12:00 — 23:00",
                prices: [{ label: "Будни / воскресенье", value: "2 000 000 ₽" }, { label: "Пятница / суббота", value: "3 000 000 ₽" }],
                note: "Продление от 50 000 ₽/час",
              },
              {
                num: "06",
                title: "Зал 1 этаж (полное закрытие)",
                sub: null,
                prices: [{ label: "Будни / воскресенье", value: "800 000 ₽" }, { label: "Пятница / суббота", value: "1 000 000 ₽" }],
              },
              {
                num: "07",
                title: "Зона у сцены",
                sub: "до 45 гостей",
                prices: [{ label: "Депозит на 4 часа", value: "280 000 ₽" }],
              },
              {
                num: "08",
                title: "Депозит на зоны столов",
                sub: "№121–126 · №103–109",
                prices: [{ label: "Депозит", value: "50 000 ₽" }],
              },
              {
                num: "09",
                title: "Банкет в любой зоне",
                sub: null,
                prices: [{ label: "Минимальный депозит", value: "2 500 ₽/чел." }],
              },
            ].map((zone) => (
              <div key={zone.num} className="bg-background p-8 group hover:bg-card transition-colors">
                <div className="font-serif text-3xl font-light text-primary/20 mb-4 group-hover:text-primary/40 transition-colors">{zone.num}</div>
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.15em] text-accent mb-1">{zone.title}</h3>
                {zone.sub && <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider mb-4">{zone.sub}</p>}
                {!zone.sub && <div className="mb-4" />}
                <div className="space-y-2">
                  {zone.prices.map((p) => (
                    <div key={p.label} className="flex justify-between items-baseline gap-4">
                      <span className="text-xs text-muted-foreground font-light">{p.label}</span>
                      <span className="text-sm text-foreground font-medium whitespace-nowrap">{p.value}</span>
                    </div>
                  ))}
                </div>
                {zone.note && <p className="text-[10px] text-primary/50 mt-3 font-light">{zone.note}</p>}
              </div>
            ))}
          </div>

          <div className="mt-8 border border-primary/15 p-6 bg-background/50">
            <p className="text-[10px] tracking-[0.15em] uppercase text-primary/50 mb-3">Дополнительные условия</p>
            <ul className="space-y-1.5">
              {[
                "Все суммы являются минимальными и могут быть увеличены в зависимости от сезона, спроса, артиста и даты.",
                "Бронирование считается подтверждённым после внесения предоплаты.",
                "Аренда оборудования, артисты, техническое обеспечение и декор обсуждаются отдельно.",
                "Администрация оставляет за собой право корректировать условия в индивидуальном порядке.",
              ].map((t, i) => (
                <li key={i} className="text-xs text-muted-foreground font-light flex gap-3">
                  <span className="text-primary/40 shrink-0">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── BANQUET CALCULATOR ───────────────────────────────────────────── */}
      <section className="py-24 bg-background relative z-10" id="banquet-form">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">

          <div className="text-center mb-14">
            <p className="text-[9px] tracking-[0.35em] text-primary uppercase mb-4">Калькулятор</p>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-4">Рассчитайте стоимость</h2>
            <p className="text-sm text-muted-foreground font-light">Ориентировочный бюджет за 30 секунд — без звонков и ожидания</p>
          </div>

          {/* Step indicator — 3 steps */}
          <div className="flex items-center justify-center gap-3 mb-12">
            {(["calc", "details", "form"] as const).map((s, i) => {
              const steps = ["calc", "details", "form"];
              const currentIdx = steps.indexOf(calcStep);
              const thisIdx = i;
              const isDone = currentIdx > thisIdx;
              const isActive = calcStep === s;
              const labels = ["Параметры", "Детали", "Контакты"];
              return (
                <div key={s} className="flex items-center gap-3">
                  <div className={`flex items-center gap-2 ${isActive ? "text-accent" : isDone ? "text-primary/50" : "text-muted-foreground/30"}`}>
                    <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-[10px] font-medium transition-colors ${isActive ? "border-accent bg-primary/20" : isDone ? "border-primary/40 bg-primary/10" : "border-primary/15"}`}>
                      {isDone ? "✓" : i + 1}
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase hidden sm:inline">{labels[i]}</span>
                  </div>
                  {i < 2 && <div className="w-8 h-[1px] bg-primary/20" />}
                </div>
              );
            })}
          </div>

          {calcStep === "calc" ? (
            <div className="border border-primary/20 bg-card p-5 sm:p-8 md:p-12">

              {/* Event type */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Тип события</p>
                <div className="flex flex-wrap gap-3">
                  {EVENT_TYPES.map(t => (
                    <button key={t} onClick={() => setCalcEventType(t)}
                      className={`px-5 py-2.5 text-xs tracking-wide border transition-all ${calcEventType === t ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Format */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Формат мероприятия</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {FORMATS.map(f => (
                    <button key={f} onClick={() => setCalcFormat(f)}
                      className={`px-5 py-4 text-xs tracking-wide border transition-all text-left ${calcFormat === f ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}>
                      <span className="block font-medium mb-1">{f}</span>
                      <span className="text-[10px] opacity-60">
                        {f === "Банкет с рассадкой" ? "Рассадка гостей за столами" : f === "Фуршет" ? "Стоячий формат, без рассадки" : "Приветственный коктейль, общение"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Guests slider */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-[9px] tracking-[0.3em] uppercase text-primary">Количество гостей</p>
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-3xl text-accent">{calcGuests}</span>
                    <span className="text-xs text-muted-foreground">чел.</span>
                  </div>
                </div>
                <input type="range" min={10} max={300} step={5} value={calcGuests}
                  onChange={e => setCalcGuests(Number(e.target.value))}
                  className="w-full accent-primary h-[2px] bg-primary/20 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground/40 mt-2">
                  <span>10</span><span>75</span><span>150</span><span>225</span><span>300</span>
                </div>
                <p className="text-[11px] text-primary/60 mt-3 tracking-wide">📍 {getHallName(calcGuests)}</p>
              </div>

              {/* Day type */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">День проведения</p>
                <div className="grid grid-cols-2 gap-3">
                  {([["weekday", "Будний день", "Пн–Чт · скидка ~15%"], ["weekend", "Выходной", "Пт–Вс · стандартный тариф"]] as const).map(([val, label, sub]) => (
                    <button key={val} onClick={() => setCalcDayType(val)}
                      className={`px-5 py-4 border transition-all text-left ${calcDayType === val ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                      <span className="block text-xs font-medium mb-1">{label}</span>
                      <span className="text-[10px] opacity-60">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Extras */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Дополнительные услуги</p>
                <div className="flex flex-wrap gap-3">
                  {EXTRAS_LIST.map(e => (
                    <button key={e} onClick={() => toggleExtra(e)}
                      className={`px-4 py-2 text-xs border transition-all ${calcExtras.includes(e) ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                      {calcExtras.includes(e) ? "✓ " : ""}{e}
                    </button>
                  ))}
                </div>
              </div>

              {/* Live price result */}
              <div className="border border-primary/30 bg-background p-6 md:p-8 mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <p className="text-[9px] tracking-[0.3em] uppercase text-primary/60 mb-2">Ориентировочный бюджет</p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-3xl md:text-4xl text-accent">
                        от {calcPrice().min.toLocaleString("ru-RU")} ₽
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground/60 mt-1">
                      до {calcPrice().max.toLocaleString("ru-RU")} ₽ · включая аренду зала и питание
                    </p>
                  </div>
                  <div className="text-[10px] text-muted-foreground/50 leading-relaxed max-w-xs">
                    Расчёт приблизительный. Точная стоимость зависит от выбора меню и финальной программы. Менеджер уточнит детали.
                  </div>
                </div>
              </div>

              <button onClick={goToDetails}
                className="w-full bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-primary-foreground text-[10px] py-5 uppercase tracking-[0.3em] font-medium transition-colors">
                Далее — детали мероприятия →
              </button>
            </div>

          ) : calcStep === "details" ? (
            <div className="border border-primary/20 bg-card p-5 sm:p-8 md:p-12">

              {/* Mini summary from step 1 */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-10 pb-6 border-b border-primary/15">
                <p className="text-xs text-muted-foreground/60 font-light leading-relaxed">
                  {calcEventType} · {calcFormat} · {calcGuests} гостей · {calcDayType === "weekend" ? "выходной" : "будний"} · <span className="text-accent">от {calcPrice().min.toLocaleString("ru-RU")} ₽</span>
                </p>
                <button onClick={() => setCalcStep("calc")} className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/40 hover:text-accent transition-colors shrink-0 self-start sm:self-center">← Шаг 1</button>
              </div>

              {/* Menu type */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Тип меню</p>
                <div className="flex flex-wrap gap-3">
                  {["Смешанное", "Мясное", "Рыбное / морепродукты", "Вегетарианское"].map(t => (
                    <button key={t} onClick={() => setMenuType(t)}
                      className={`px-4 py-2.5 text-xs border transition-all ${menuType === t ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Courses */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Количество подач</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ["2 блюда", "Горячее + десерт"],
                    ["3 блюда", "Закуски + горячее + десерт"],
                    ["4 блюда", "Салаты + горячее + гарнир + десерт"],
                    ["5 блюд", "Полный дегустационный формат"],
                  ].map(([c, sub]) => (
                    <button key={c} onClick={() => setMenuCourses(c)}
                      className={`px-4 py-4 border text-left transition-all ${menuCourses === c ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                      <span className="block text-xs font-medium mb-1">{c}</span>
                      <span className="text-[10px] opacity-60">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Welcome cocktail */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Приветственный коктейль</p>
                <div className="grid grid-cols-2 gap-3">
                  {([[true, "Да", "Гости встречаются за напитками до начала банкета"], [false, "Нет", "Гости сразу рассаживаются за столы"]] as const).map(([val, label, sub]) => (
                    <button key={String(val)} onClick={() => setWelcomeCocktail(val)}
                      className={`px-4 py-4 border text-left transition-all ${welcomeCocktail === val ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                      <span className="block text-xs font-medium mb-1">{label}</span>
                      <span className="text-[10px] opacity-60">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Seating — only for seated format */}
              {calcFormat === "Банкет с рассадкой" && (
                <>
                  <div className="mb-10">
                    <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Тип рассадки</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        ["Круглые столы", "Классика — уютные группы гостей"],
                        ["Банкетный стол", "Один длинный стол, все вместе"],
                        ["П-образный", "Торжественный, для выступлений"],
                      ].map(([t, sub]) => (
                        <button key={t} onClick={() => setSeatingType(t)}
                          className={`px-4 py-4 border text-left transition-all ${seatingType === t ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                          <span className="block text-xs font-medium mb-1">{t}</span>
                          <span className="text-[10px] opacity-60">{sub}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {seatingType === "Круглые столы" && (
                    <div className="mb-10">
                      <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Мест за каждым столом</p>
                      <div className="flex flex-wrap gap-3">
                        {["6 мест", "8 мест", "10 мест"].map(n => (
                          <button key={n} onClick={() => setSeatingPerTable(n)}
                            className={`px-5 py-2.5 text-xs border transition-all ${seatingPerTable === n ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Timing */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Тайминг</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-[10px] text-muted-foreground/60 mb-3">Время начала</p>
                    <div className="flex flex-wrap gap-2">
                      {["12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"].map(t => (
                        <button key={t} onClick={() => setStartTime(t)}
                          className={`px-3 py-1.5 text-xs border transition-all ${startTime === t ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/40"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground/60 mb-3">Продолжительность</p>
                    <div className="flex flex-wrap gap-2">
                      {["2 часа","3 часа","4 часа","5 часов","6 часов","7+ часов"].map(d => (
                        <button key={d} onClick={() => setDuration(d)}
                          className={`px-3 py-1.5 text-xs border transition-all ${duration === d ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/40"}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Entertainment */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Развлекательная программа</p>
                <div className="grid grid-cols-2 gap-3">
                  {([[true, "Да", "Ведущий, конкурсы, выступления"], [false, "Нет", "Только музыка, без программы"]] as const).map(([val, label, sub]) => (
                    <button key={String(val)} onClick={() => setEntertainment(val)}
                      className={`px-4 py-4 border text-left transition-all ${entertainment === val ? "border-accent bg-primary/20 text-accent" : "border-primary/20 text-muted-foreground hover:border-primary/50"}`}>
                      <span className="block text-xs font-medium mb-1">{label}</span>
                      <span className="text-[10px] opacity-60">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Special requirements */}
              <div className="mb-10">
                <p className="text-[9px] tracking-[0.3em] uppercase text-primary mb-5">Особые требования <span className="text-muted-foreground/40 normal-case tracking-normal text-[10px]">— необязательно</span></p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] text-muted-foreground/60 block mb-2">Аллергии / диетические ограничения</label>
                    <textarea value={allergies} onChange={e => setAllergies(e.target.value)} rows={2}
                      placeholder="Например: нет глютена у 3 гостей, вегетарианцы..."
                      className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-sm focus:outline-none focus:border-primary transition-colors resize-none placeholder:text-muted-foreground/30" />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground/60 block mb-2">VIP-гости / особые пожелания</label>
                    <textarea value={vipNote} onChange={e => setVipNote(e.target.value)} rows={2}
                      placeholder="Например: именинник в центре, место для инвалидной коляски..."
                      className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-sm focus:outline-none focus:border-primary transition-colors resize-none placeholder:text-muted-foreground/30" />
                  </div>
                </div>
              </div>

              <button onClick={goToContactForm}
                className="w-full bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-primary-foreground text-[10px] py-5 uppercase tracking-[0.3em] font-medium transition-colors">
                Далее — оставить контакты →
              </button>
            </div>

          ) : createBanquet.isSuccess ? (
            <div className="border border-primary/30 p-8 md:p-16 bg-card text-center">
              <div className="w-12 h-12 border border-accent rounded-full flex items-center justify-center mx-auto mb-6 text-accent text-xl">✓</div>
              <h3 className="text-3xl font-serif font-light text-primary mb-4">Заявка принята</h3>
              <p className="text-sm font-light text-muted-foreground mb-8">Банкетный менеджер свяжется с вами в ближайшее время.</p>
              <button className="text-[9px] tracking-[0.3em] uppercase text-accent border border-primary/40 px-8 py-3 hover:bg-primary hover:text-primary-foreground transition-all"
                onClick={() => { createBanquet.reset(); setCalcStep("calc"); }}>
                Новый расчёт
              </button>
            </div>

          ) : (
            <div className="border border-primary/20 bg-card p-8 md:p-12">

              {/* Summary card */}
              <div className="border border-accent/30 bg-primary/10 p-5 mb-10">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="text-[9px] tracking-[0.3em] uppercase text-accent/70 mb-2">Ваш бриф</p>
                    <p className="text-sm text-foreground font-light">
                      <span className="font-medium">{calcEventType}</span> · {calcFormat} · {calcGuests} гостей · {calcDayType === "weekend" ? "выходной" : "будний"}
                    </p>
                    <p className="text-accent font-serif text-xl mt-1">
                      от {calcPrice().min.toLocaleString("ru-RU")} — до {calcPrice().max.toLocaleString("ru-RU")} ₽
                    </p>
                  </div>
                  <button onClick={() => setCalcStep("details")}
                    className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground/50 border border-primary/20 px-4 py-2 hover:text-accent hover:border-accent/40 transition-all whitespace-nowrap shrink-0">
                    ← Изменить
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 pt-3 border-t border-primary/15">
                  {([
                    ["Меню", `${menuType}, ${menuCourses}`],
                    ["Коктейль", welcomeCocktail ? "Да" : "Нет"],
                    ...(calcFormat === "Банкет с рассадкой" ? [["Рассадка", `${seatingType}${seatingType === "Круглые столы" ? `, ${seatingPerTable}` : ""}`]] : []),
                    ["Начало", startTime],
                    ["Длительность", duration],
                    ["Программа", entertainment ? "Да" : "Нет"],
                  ] as string[][]).map(([label, val]) => (
                    <div key={label} className="py-0.5">
                      <span className="text-[9px] text-muted-foreground/50 uppercase tracking-wide">{label}</span>
                      <span className="text-[11px] text-foreground/70 ml-2">{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <FormField control={form.control} name="name" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-normal">Имя</FormLabel>
                        <FormControl><input className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-lg focus:outline-none focus:border-primary transition-colors" {...field} /></FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="phone" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-normal">Телефон</FormLabel>
                        <FormControl><input type="tel" className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-lg focus:outline-none focus:border-primary transition-colors" {...field} /></FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="date" render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-normal">Ориентировочная дата</FormLabel>
                        <FormControl><input placeholder="Например: август 2025 или конкретная дата" className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-lg focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/30" {...field} /></FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="comment" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-normal">Дополнительные пожелания</FormLabel>
                      <FormControl>
                        <textarea className="w-full bg-transparent border-b border-primary/20 pb-2 text-foreground font-light text-sm focus:outline-none focus:border-primary transition-colors resize-none h-16 leading-relaxed" {...field} />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="consent" render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start gap-3">
                        <FormControl>
                          <input type="checkbox" id="consent" checked={field.value} onChange={field.onChange}
                            className="mt-1 w-4 h-4 accent-primary shrink-0 cursor-pointer" />
                        </FormControl>
                        <label htmlFor="consent" className="text-xs font-light text-muted-foreground leading-relaxed cursor-pointer">
                          Я даю согласие на обработку персональных данных в соответствии с{" "}
                          <Link href="/privacy" className="text-primary hover:text-accent transition-colors underline underline-offset-2">политикой конфиденциальности</Link>
                        </label>
                      </div>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )} />
                  <button type="submit" disabled={createBanquet.isPending}
                    className="w-full bg-primary/20 border border-primary text-accent hover:bg-primary hover:text-primary-foreground text-[10px] py-5 uppercase tracking-[0.3em] font-medium transition-colors disabled:opacity-50">
                    {createBanquet.isPending ? "Отправка..." : "Отправить заявку"}
                  </button>
                </form>
              </Form>
            </div>
          )}
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/banket"]} title="Вопросы о банкетах" />
    </div>
  );
}
